/**
 * What the browser knows before the server has answered — ROADMAP 9.26.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE FILL IS NOT OURS TO MAKE FASTER
 *
 * Measured in production on 2026-11-03: one book sweep is ~2 seconds and
 * `poller.poll`, the only step that learns a fill, runs last behind three
 * other broker round trips. Two attempts to shorten that changed nothing —
 * waking the poller earlier (9.22) and sweeping accounts concurrently
 * (9.25) both help the ENGINE and neither reaches below the broker's own
 * book latency. It is XTS's number, not ours.
 *
 * So the row cannot wait for it. The browser already knows everything a
 * position row needs at the instant the button goes down — the contract,
 * the side, the lots, the account — and that knowledge is not a guess: it
 * is what we are about to ask for. This store holds it until the server
 * confirms, and the drawer draws it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PROVISIONAL IS NOT THE SAME AS FAKED, AND THE LINE IS THE PRICE
 *
 * A provisional row carries the contract, the side and the ORDERED
 * quantity, and nothing else. It does not carry an entry price, a P&L or a
 * mark, because those are the broker's to report and we do not know them —
 * inventing one would put a number in DAY MTM and OPEN RISK that nobody
 * traded, and a rejected order would leave a trader having read a profit on
 * a position that never existed.
 *
 * That is the same line `bookPush.ts` draws server-side, and it is why the
 * answer to "just fake it as executed" is a row that says WORKING rather
 * than a row that says filled.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EVERY PROVISIONAL ROW MUST DIE, AND THERE ARE THREE WAYS
 *
 *   CONFIRMED  the server pushes the real trade carrying `forRequest`, and
 *              the real row replaces this one exactly. No guessing which.
 *   REFUSED    the submit came back `ok: false`. Nothing was sent, so the
 *              row goes immediately.
 *   EXPIRED    neither happened within `PROVISIONAL_TTL_MS`. A notice can
 *              be lost — a dropped socket, a reconnect — and a row that
 *              outlived its evidence is a position the trader does not
 *              have. The book's own 5-second poll is the truth it falls
 *              back to.
 *
 * The third is the one that matters: without it a lost push leaves a
 * phantom position on screen forever, which is strictly worse than the
 * two-second wait this removes.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** How long a row may stand without the server confirming it. */
export const PROVISIONAL_TTL_MS = 12_000

export interface ProvisionalLeg {
  readonly token: number
  readonly side: 'BUY' | 'SELL'
  /** UNITS, already multiplied. What the trader asked for. */
  readonly qty: number
  readonly lotSize: number
  readonly strike: number
  readonly optionType: string
  readonly expiry: string
  /** Built by the same `contractLabel` the drawer uses. */
  readonly symbol: string
}

export interface Provisional {
  /** The click. Also the key the server echoes back as `forRequest`. */
  readonly clientRequestId: string
  readonly connectionId: string
  readonly brokerLabel: string
  readonly mode: string
  readonly underlying: string
  readonly legs: readonly ProvisionalLeg[]
  /** Unix ms, for the expiry sweep. */
  readonly at: number
}

/**
 * Drop whatever has outlived its evidence.
 *
 * PURE, and separated from the store so the rule can be tested without a
 * clock: "a row disappears after twelve seconds" is a claim about a
 * trader's screen, and a test that had to wait twelve seconds to check it
 * is a test nobody runs.
 */
export function prune(
  rows: readonly Provisional[],
  now: number,
  ttl: number = PROVISIONAL_TTL_MS,
): Provisional[] {
  return rows.filter((r) => now - r.at < ttl)
}

/**
 * Is this server push the confirmation of a provisional row?
 *
 * ON `forRequest` ALONE. Correlating on contract and account instead would
 * guess wrong the moment a trader clicks the same strike twice in a
 * second — which is exactly the case that made an instant row worth
 * building.
 */
export const confirms = (row: Provisional, forRequest: string | undefined): boolean =>
  forRequest !== undefined && row.clientRequestId === forRequest

type Listener = () => void

class ProvisionalStore {
  private rows: Provisional[] = []
  /** Legs the trader has asked to close, until the book agrees. */
  private closing = new Set<string>()
  private readonly listeners = new Set<Listener>()
  /** The snapshot handed to React. Replaced only when something changed. */
  private snap: { rows: readonly Provisional[]; closing: ReadonlySet<string> } = {
    rows: [],
    closing: new Set(),
  }

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  /**
   * `useSyncExternalStore` compares by IDENTITY and re-renders on every
   * change of it, so this must return the SAME object until something
   * actually moves — a fresh one per call is an infinite render loop.
   */
  getSnapshot = (): { rows: readonly Provisional[]; closing: ReadonlySet<string> } => this.snap

  /** A click, the moment it happens and before anything is sent. */
  place(row: Provisional): void {
    this.rows = [...prune(this.rows, row.at), row]
    this.publish()
  }

  /** Confirmed, refused, or abandoned. Idempotent. */
  settle(clientRequestId: string): void {
    const next = this.rows.filter((r) => r.clientRequestId !== clientRequestId)
    if (next.length === this.rows.length) return
    this.rows = next
    this.publish()
  }

  /** Retire whatever this push confirms. Returns true when one was retired. */
  confirm(forRequest: string | undefined): boolean {
    if (forRequest === undefined) return false
    const next = this.rows.filter((r) => !confirms(r, forRequest))
    if (next.length === this.rows.length) return false
    this.rows = next
    this.publish()
    return true
  }

  /** Drop anything that has outlived its evidence. Safe to call often. */
  sweep(now: number = Date.now()): void {
    const next = prune(this.rows, now)
    if (next.length === this.rows.length) return
    this.rows = next
    this.publish()
  }

  /** The trader asked to close this leg; the book has not caught up. */
  markClosing(tradeLegIds: readonly string[]): void {
    let changed = false
    for (const id of tradeLegIds) {
      if (!this.closing.has(id)) {
        this.closing.add(id)
        changed = true
      }
    }
    if (changed) this.publish()
  }

  /** The exit settled, or failed. Either way this leg is the book's again. */
  clearClosing(tradeLegIds: readonly string[]): void {
    let changed = false
    for (const id of tradeLegIds) if (this.closing.delete(id)) changed = true
    if (changed) this.publish()
  }

  private publish(): void {
    this.snap = { rows: this.rows, closing: new Set(this.closing) }
    for (const fn of this.listeners) fn()
  }
}

export const provisionalStore = new ProvisionalStore()
